/**
 * Phase 3.27 — Werbemittel & Tracking Sektion.
 * 4 Toggles (Banner / Landingpage / QR-Smiley / B2C-Ads) mit Previews + Downloads.
 * Backend liefert dynamische SVG-Banner + HTML-Landingpage mit Transaktionscode
 * für serverseitiges Cookie-Attribution-Tracking.
 */
import { useState } from "react";
import { Copy, Download, ExternalLink, Smile, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { CheckRow } from "./_shared";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";

const BANNER_FORMATS = [
    { key: "leaderboard", w: 728, h: 90, label: "Leaderboard (728×90)" },
    { key: "rectangle", w: 300, h: 250, label: "Medium Rectangle (300×250)" },
    { key: "skyscraper", w: 160, h: 600, label: "Skyscraper (160×600)" },
    { key: "mobile", w: 320, h: 50, label: "Mobile Banner (320×50)" },
];

export function WerbemittelSection({ currentUser, hideQrSmiley }) {
    const { refresh } = useAuth();
    const code = currentUser?.customer_number ? `H_${currentUser.customer_number}` : null;
    const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        marketing_banners_enabled: !!currentUser?.marketing_banners_enabled,
        marketing_landing_enabled: !!currentUser?.marketing_landing_enabled,
        marketing_qr_smiley_enabled: !!currentUser?.marketing_qr_smiley_enabled,
        marketing_b2c_ads_enabled: !!currentUser?.marketing_b2c_ads_enabled,
        marketing_ad_headline: currentUser?.marketing_ad_headline || "",
        marketing_ad_subline: currentUser?.marketing_ad_subline || "",
    });

    const patch = async (payload) => {
        setBusy(true);
        try {
            await api.patch("/users/me/dealer-profile", payload);
            await refresh();
            toast.success("Gespeichert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const toggle = (key) => {
        const next = !form[key];
        setForm((p) => ({ ...p, [key]: next }));
        patch({ [key]: next });
    };

    const saveAdCopy = () => {
        patch({
            marketing_ad_headline: form.marketing_ad_headline?.trim() || null,
            marketing_ad_subline: form.marketing_ad_subline?.trim() || null,
        });
    };

    const copy = async (text) => {
        try { await navigator.clipboard.writeText(text); toast.success("Kopiert"); }
        catch { toast.error("Bitte manuell markieren"); }
    };

    if (!code) {
        return (
            <div className="text-xs text-gray-500 italic" data-testid="werbemittel-no-code">
                Werbemittel werden verfügbar, sobald ein Transaktions-Code zugewiesen ist.
            </div>
        );
    }

    const landingUrl = `${apiBase}/marketing/landing/${code}`;
    const trackUrl = `${apiBase}/marketing/t/${code}`;

    return (
        <div className="space-y-4" data-testid="werbemittel-section">
            <div className="bg-gray-50 border border-gray-200 rounded-sm p-4">
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">Anzeigentext (für alle Werbemittel)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Headline</Label>
                        <Input
                            value={form.marketing_ad_headline}
                            onChange={(e) => setForm((p) => ({ ...p, marketing_ad_headline: e.target.value }))}
                            placeholder={currentUser?.company_name || "Ihr Firmenname bei twowheels.info"}
                            className="rounded-sm mt-1 text-sm"
                            disabled={busy}
                            data-testid="werbemittel-headline"
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Subline</Label>
                        <Input
                            value={form.marketing_ad_subline}
                            onChange={(e) => setForm((p) => ({ ...p, marketing_ad_subline: e.target.value }))}
                            placeholder="Motorräder & Fahrräder · Geprüfte Händler"
                            className="rounded-sm mt-1 text-sm"
                            disabled={busy}
                            data-testid="werbemittel-subline"
                        />
                    </div>
                </div>
                <Button
                    onClick={saveAdCopy}
                    disabled={busy}
                    size="sm"
                    className="mt-3 bg-[#111827] hover:bg-black text-white rounded-sm font-bold uppercase tracking-wider text-xs"
                    data-testid="werbemittel-save-copy"
                >Texte speichern</Button>
            </div>

            <div className="border border-gray-200 rounded-sm" data-testid="werbemittel-banners">
                <CheckRow
                    label="Bannerwerbung aktivieren"
                    hint="Erzeugt SVG-Banner in 4 Standardformaten mit eingebettetem Transaktions-Code."
                    checked={form.marketing_banners_enabled}
                    disabled={busy}
                    onChange={() => toggle("marketing_banners_enabled")}
                    testId="werbemittel-banners-checkbox"
                />
                {form.marketing_banners_enabled && (
                    <div className="border-t border-gray-100 px-5 pb-4 pt-3 bg-gray-50/50">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {BANNER_FORMATS.map((f) => {
                                const url = `${apiBase}/marketing/banner/${code}/${f.key}.svg`;
                                return (
                                    <div key={f.key} className="bg-white border border-gray-200 rounded-sm p-3" data-testid={`werbemittel-banner-${f.key}`}>
                                        <div className="text-[11px] font-bold text-gray-700 mb-2">{f.label}</div>
                                        <div className="bg-gray-50 border border-gray-100 rounded-sm overflow-hidden flex items-center justify-center" style={{ minHeight: 80 }}>
                                            <img src={url} alt={f.label} className="max-w-full max-h-[180px]" />
                                        </div>
                                        <div className="flex gap-2 mt-2">
                                            <Button size="sm" variant="outline" onClick={() => copy(url)} className="rounded-sm text-xs flex-1" data-testid={`werbemittel-banner-${f.key}-copy`}>
                                                <Copy className="w-3 h-3 mr-1" /> URL
                                            </Button>
                                            <a href={url} download={`twowheels-banner-${f.key}-${code}.svg`} className="inline-flex items-center justify-center px-3 py-1.5 bg-[#111827] hover:bg-black text-white rounded-sm text-xs font-bold flex-1" data-testid={`werbemittel-banner-${f.key}-download`}>
                                                <Download className="w-3 h-3 mr-1" /> SVG
                                            </a>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-3 text-[10px] text-gray-500">
                            Banner-Klicks landen auf <code className="bg-gray-100 px-1 rounded font-mono-num">{trackUrl}</code> — der Klick wird als Attribution gespeichert und ein 30-Tage-Cookie gesetzt.
                        </div>
                    </div>
                )}
            </div>

            <div className="border border-gray-200 rounded-sm" data-testid="werbemittel-landing">
                <CheckRow
                    label="Landingpage aktivieren"
                    hint="Eigene Mini-Landingpage mit Ihrem Transaktions-Code für Print-/Offline-Kampagnen."
                    checked={form.marketing_landing_enabled}
                    disabled={busy}
                    onChange={() => toggle("marketing_landing_enabled")}
                    testId="werbemittel-landing-checkbox"
                />
                {form.marketing_landing_enabled && (
                    <div className="border-t border-gray-100 px-5 pb-4 pt-3 bg-gray-50/50">
                        <div className="bg-white border border-gray-200 rounded-sm p-3">
                            <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Landing-URL</Label>
                            <div className="flex gap-2 mt-1">
                                <Input value={landingUrl} readOnly className="rounded-sm font-mono-num text-xs" data-testid="werbemittel-landing-url" />
                                <Button size="sm" onClick={() => copy(landingUrl)} className="rounded-sm bg-[#111827] hover:bg-black text-white text-xs" data-testid="werbemittel-landing-copy">
                                    <Copy className="w-3 h-3" />
                                </Button>
                                <a href={landingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-3 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] rounded-sm text-xs font-bold" data-testid="werbemittel-landing-open">
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                            </div>
                            <div className="mt-2 text-[10px] text-gray-500">
                                Diese URL kann auf Visitenkarten, in QR-Codes oder in E-Mail-Kampagnen verwendet werden.
                                Jeder Besucher wird über <span className="font-mono-num">{code}</span> Ihnen zugeordnet.
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className={`border border-gray-200 rounded-sm ${hideQrSmiley ? "hidden" : ""}`} data-testid="werbemittel-qrsmiley">
                <CheckRow
                    label="QR-Smiley-Aktion aktivieren"
                    hint="Printbares Smiley-Logo mit Transaktions-Code + dynamischer QR-Code für Aufkleber/Aufsteller."
                    checked={form.marketing_qr_smiley_enabled}
                    disabled={busy}
                    onChange={() => toggle("marketing_qr_smiley_enabled")}
                    testId="werbemittel-qrsmiley-checkbox"
                />
                {form.marketing_qr_smiley_enabled && (
                    <div className="border-t border-gray-100 px-5 pb-4 pt-3 bg-gray-50/50 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-white border border-gray-200 rounded-sm p-3 text-center">
                            <div className="text-[11px] font-bold text-gray-700 mb-2 flex items-center gap-1 justify-center">
                                <Smile className="w-3.5 h-3.5 text-[#FFCC00]" /> Smiley + Code
                            </div>
                            <img src={`${apiBase}/marketing/qr-smiley/${code}.svg`} alt="QR-Smiley" className="mx-auto" style={{ width: 180, height: 180 }} />
                            <a href={`${apiBase}/marketing/qr-smiley/${code}.svg`} download={`twowheels-smiley-${code}.svg`} className="inline-flex items-center justify-center mt-2 px-3 py-1.5 bg-[#111827] hover:bg-black text-white rounded-sm text-xs font-bold" data-testid="werbemittel-smiley-download">
                                <Download className="w-3 h-3 mr-1" /> Smiley SVG
                            </a>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-sm p-3 text-center">
                            <div className="text-[11px] font-bold text-gray-700 mb-2 flex items-center gap-1 justify-center">
                                <QrCode className="w-3.5 h-3.5 text-[#111827]" /> QR-Code (Landingpage)
                            </div>
                            <div className="inline-block p-3 bg-white border border-gray-200 rounded-sm" data-testid="werbemittel-qr-wrap">
                                <QRCodeSVG value={landingUrl} size={150} level="H" fgColor="#111827" bgColor="#FFFFFF" />
                            </div>
                            <div className="mt-2 text-[10px] text-gray-500 font-mono-num">{code}</div>
                        </div>
                    </div>
                )}
            </div>

            <div className="border border-gray-200 rounded-sm" data-testid="werbemittel-b2c">
                <CheckRow
                    label="Werbung auf twowheels.info schalten"
                    hint="Ihre Anzeige erscheint a) in der Fahrzeug-Übersicht und b) auf einzelnen Fahrzeug-Seiten."
                    checked={form.marketing_b2c_ads_enabled}
                    disabled={busy}
                    onChange={() => toggle("marketing_b2c_ads_enabled")}
                    testId="werbemittel-b2c-checkbox"
                />
                {form.marketing_b2c_ads_enabled && (
                    <div className="border-t border-gray-100 px-5 pb-4 pt-3 bg-gray-50/50">
                        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 p-3 border-b border-gray-100">
                                Vorschau Ihrer Anzeige
                            </div>
                            <div className="p-3">
                                <div className="bg-[#111827] text-white rounded-sm p-4 flex items-center gap-4">
                                    {currentUser?.company_logo_url && (
                                        <img src={currentUser.company_logo_url} alt="Logo" className="w-12 h-12 rounded-sm bg-white object-contain" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[9px] uppercase tracking-widest font-bold text-[#FFCC00]">Werbung · Anzeige</div>
                                        <div className="font-bold text-sm truncate">{form.marketing_ad_headline || currentUser?.company_name || "Ihr Firmenname"}</div>
                                        <div className="text-xs text-gray-300 truncate">{form.marketing_ad_subline || "Motorräder & Fahrräder · Geprüfte Händler"}</div>
                                    </div>
                                    <span className="px-3 py-1.5 bg-[#FFCC00] text-[#111827] rounded-sm text-[10px] font-black uppercase tracking-wider">Ansehen</span>
                                </div>
                            </div>
                        </div>
                        <div className="mt-2 text-[10px] text-gray-500">
                            Anzeigen rotieren auf der Inserate-Übersicht und auf jeder Inseratsseite.
                            Klicks werden über <span className="font-mono-num">{code}</span> Ihnen zugeordnet.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
